// MultiRecordTray.js — the V0.5 MultiRecord++ panel (trayMode 'multirecord').
// A self-contained tray: vertical day timeline + a big, in-your-face record
// button (this surface is basically the whole app, so the primary action is
// prominent, not a header icon) + the schedule loader. No ambient brain, no
// queue, no chat. Shares the emerge/portal shell with the ambient panel.
// Design: docs/comprehend-live-v05.md.
import React, { useEffect, useState } from 'react';
import { useComprehendLive } from 'agent/useComprehendLive';
import { computeActiveSet } from 'agent/multiRecord';
import VerticalTimeline from './VerticalTimeline';
import ScheduleLoader from './ScheduleLoader';
import LiveWave from './LiveWave';
import ViewToggle from './ViewToggle';
import { T, buttonGhost } from './liveTheme';

const bufferInput = {
  width: 42,
  background: T.surfaceDeep,
  border: `1px solid ${T.border}`,
  borderRadius: 6,
  color: T.textPrimary,
  fontSize: 11,
  padding: '3px 5px',
  textAlign: 'center',
};

const MultiRecordTray = () => {
  const { state, actions, apiClient } = useComprehendLive();
  const [loaderOpen, setLoaderOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [, force] = useState(0);

  // Tick so the count tracks the active window in real time.
  useEffect(() => {
    const t = setInterval(() => force((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);

  const day = state.recordingDay;
  // What's actually being recorded right now. Empty until you hit record (the
  // day must be on); then it's the effective set (current window + manual
  // overrides) — the SAME source the timeline highlights, so the names here
  // and the red cards always agree.
  const activeIds = day
    ? computeActiveSet(state.schedule, Date.now(), state.manualOverrides, {
        beforeMs: (state.bufferBeforeMin || 0) * 60000,
        afterMs: (state.bufferAfterMin || 0) * 60000,
        auto: state.autoSwitch,
      })
    : [];
  const recordingCount = activeIds.length;
  const autoEnabled = day && state.autoSwitch;
  const waiting = day && state.autoSwitch && recordingCount === 0;
  const recording = day && recordingCount > 0;

  // Display label per active visit — same rule the timeline uses
  // (session name first, then the schedule slot's patient name).
  const labelForId = (id) => {
    const session = (state.sessions || []).find(
      (s) => String(s.conversationId) === String(id),
    );
    if (session?.name) return session.name;
    for (const slot of state.schedule || []) {
      for (const p of slot.patients || []) {
        if (String(p.conversationId) === String(id)) {
          return p.patientName || 'Untitled visit';
        }
      }
    }
    return 'Untitled visit';
  };
  const recordingNames = activeIds.map(labelForId);

  return (
    <div
      style={{
        background: T.surface,
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        maxWidth: '100%',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        animation: 'cplSlide .18s ease',
        boxShadow: '0 12px 24px rgba(13,35,30,0.45)',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 7,
          padding: '10px 12px 8px',
          flexShrink: 0,
        }}
      >
        <span
          className={recording ? 'cpl-dot' : undefined}
          style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: recording ? T.red : waiting ? T.amber : T.textFaint,
          }}
        />
        <span
          role="status"
          aria-live="polite"
          style={{
            color: recording ? T.redSoft : waiting ? T.amber : T.greenBright,
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '.5px',
            flexShrink: 0,
          }}
        >
          {recording
            ? 'RECORDING'
            : waiting
              ? 'AUTO-RECORD ON'
              : 'MULTIRECORD'}
        </span>
        <span
          style={{
            color: T.textFaint,
            fontSize: 10,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {recording
            ? `${recordingCount} recording`
            : waiting
              ? 'Waiting for a visit'
              : 'Not recording'}
        </span>
        <div
          style={{
            marginLeft: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          {state.trayMode === 'ambient' && <ViewToggle />}
          {/* Dev-only demo toggle — flips the localStorage flag and reloads
              (demo wrapping is decided at mount). Lets you start/exit the
              scripted demo straight from the timeline. */}
          {apiClient?.isDev && (
            <span
              role="button"
              onClick={actions.toggleDemo}
              title={
                state.demo
                  ? 'Exit demo mode (reloads)'
                  : 'Start the scripted demo (reloads)'
              }
              style={{
                fontSize: 9,
                fontWeight: 700,
                letterSpacing: '.5px',
                padding: '2px 7px',
                borderRadius: 8,
                cursor: 'pointer',
                flexShrink: 0,
                background: state.demo ? T.amber : 'transparent',
                color: state.demo ? '#412402' : T.textFaint,
                border: `1px solid ${state.demo ? T.amber : T.border}`,
              }}
            >
              {state.demo ? 'DEMO ON' : 'DEMO'}
            </span>
          )}
          <button
            type="button"
            aria-label="Close tray"
            title="Close tray"
            onClick={() => actions.setExpanded(false)}
            style={{
              width: 28,
              height: 28,
              flexShrink: 0,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
              borderRadius: 9,
              border: state.expanded
                ? `1px solid ${T.border}`
                : '1px solid transparent',
              background: state.expanded
                ? 'rgba(255,255,255,0.08)'
                : 'transparent',
              color: T.textPrimary,
              fontSize: 14,
              cursor: 'pointer',
              marginRight: 6,
            }}
          >
            <i
              className={`fa fa-${state.expanded ? 'times' : 'chevron-down'}`}
              aria-hidden="true"
            />
          </button>
        </div>
      </div>

      {/* Who's recording right now — names, not just a count, so it's always
          clear who the mic is capturing. Only while the day is on. */}
      {day && (
        <div
          style={{
            flexShrink: 0,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 6,
            padding: '0 12px 8px',
          }}
        >
          {recordingNames.length > 0 ? (
            recordingNames.map((name, i) => (
              <span
                key={`${name}-${i}`}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                  maxWidth: '100%',
                  background: 'rgba(245,54,92,0.14)',
                  border: `1px solid ${T.red}`,
                  color: T.redSoft,
                  borderRadius: 999,
                  padding: '3px 9px',
                  fontSize: 11,
                  fontWeight: 600,
                }}
              >
                <span
                  className="cpl-dot"
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    background: T.red,
                    flexShrink: 0,
                  }}
                />
                <span
                  style={{
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {name}
                </span>
              </span>
            ))
          ) : (
            <span style={{ fontSize: 11, color: T.textFaint }}>
              {state.autoSwitch
                ? 'No visit in the record window right now.'
                : 'No visit selected — tap a visit to record it.'}
            </span>
          )}
        </div>
      )}

      {/* Auto-record toggle + record window. Auto is OFF by default: recording
          is manual (tap a visit) until you turn this on, then the schedule
          clock drives it within the buffer window. */}
      <div
        style={{
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
          padding: '0 12px 8px',
          fontSize: 11,
          color: T.textSecondary,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {/* Switch */}
          <button
            type="button"
            role="switch"
            aria-checked={autoEnabled}
            onClick={() => actions.setAutoRecordingEnabled(!autoEnabled)}
            title={autoEnabled ? 'Stop auto-recording' : 'Start auto-recording'}
            style={{
              position: 'relative',
              width: 32,
              height: 18,
              flexShrink: 0,
              borderRadius: 9,
              border: 'none',
              cursor: 'pointer',
              padding: 0,
              background: autoEnabled ? T.green : T.surfaceDeep,
              transition: 'background .15s ease',
            }}
          >
            <span
              style={{
                position: 'absolute',
                top: 2,
                left: autoEnabled ? 16 : 2,
                width: 14,
                height: 14,
                borderRadius: '50%',
                background: autoEnabled ? T.greenDark : T.textFaint,
                transition: 'left .15s ease',
              }}
            />
          </button>
          <span style={{ color: T.textPrimary, fontWeight: 600 }}>
            Auto-record on schedule
          </span>
          <i
            className="fa fa-info-circle"
            aria-hidden="true"
            role="button"
            title="What is this?"
            onClick={() => setInfoOpen((v) => !v)}
            style={{
              color: infoOpen ? T.greenBright : T.textFaint,
              fontSize: 12,
              cursor: 'pointer',
            }}
          />
        </div>

        {/* One-line plain-English state, always visible. */}
        {autoEnabled ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              flexWrap: 'wrap',
            }}
          >
            <span style={{ color: T.textFaint }}>Record from</span>
            <input
              type="number"
              min={0}
              value={state.bufferBeforeMin}
              onChange={(e) =>
                actions.setRecordBuffers(e.target.value, state.bufferAfterMin)
              }
              style={bufferInput}
            />
            <span style={{ color: T.textFaint }}>min before to</span>
            <input
              type="number"
              min={0}
              value={state.bufferAfterMin}
              onChange={(e) =>
                actions.setRecordBuffers(state.bufferBeforeMin, e.target.value)
              }
              style={bufferInput}
            />
            <span style={{ color: T.textFaint }}>min after each visit</span>
          </div>
        ) : (
          <span style={{ color: T.textFaint }}>
            Manual — tap a visit on the timeline to record it.
          </span>
        )}

        {infoOpen && (
          <p
            style={{
              margin: '2px 0 0',
              lineHeight: 1.45,
              color: T.textSecondary,
              background: T.surfaceDeep,
              border: `1px solid ${T.border}`,
              borderRadius: 8,
              padding: '7px 9px',
            }}
          >
            <strong style={{ color: T.textPrimary }}>Auto-record</strong> uses
            each visit&apos;s scheduled time. The mic starts{' '}
            <strong>{state.bufferBeforeMin || 0} min before</strong> a visit
            begins and keeps recording{' '}
            <strong>{state.bufferAfterMin || 0} min after</strong> it ends — so
            you never clip the opening or the handoff to the next visit. With it
            off, nothing records until you tap a visit yourself.
          </p>
        )}
      </div>

      {/* Body — the vertical day */}
      <div
        className="cpl-scroll"
        style={{
          flex: 1,
          minHeight: 0,
          minWidth: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          padding: '0 12px',
        }}
      >
        <VerticalTimeline />
      </div>
      {/* Modal — centered over the tray, not an inline panel. */}
      {loaderOpen && <ScheduleLoader onClose={() => setLoaderOpen(false)} />}

      {/* The big record button — the primary action, front and center. */}
      <div
        style={{
          position: 'relative',
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 6,
          padding: '12px 12px 16px',
          borderTop: `1px solid ${T.border}`,
        }}
      >
        <button
          onClick={() =>
            day ? actions.stopRecordingDay() : actions.startRecordingDay()
          }
          className={recording ? 'cpl-glow' : undefined}
          title={day ? 'Stop recording the day' : 'Record your day'}
          style={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: recording
              ? T.red
              : waiting
                ? 'rgba(251,191,36,0.16)'
                : T.green,
            color: recording ? '#fff' : waiting ? T.amber : T.greenDark,
            border: recording
              ? '3px solid rgba(245,54,92,0.35)'
              : waiting
                ? '3px solid rgba(251,191,36,0.45)'
                : '3px solid rgba(45,206,137,0.3)',
            boxShadow: '0 4px 14px rgba(0,0,0,0.35)',
          }}
        >
          {recording ? (
            <LiveWave color="#fff" width={40} height={26} />
          ) : waiting ? (
            <i className="fa fa-clock" aria-hidden="true" />
          ) : (
            <i
              className="fa fa-microphone"
              aria-hidden="true"
              style={{ fontSize: 24 }}
            />
          )}
        </button>
        <span style={{ fontSize: 11, color: T.textSecondary }}>
          {recording
            ? `Recording ${recordingCount} ${
                recordingCount === 1 ? 'visit' : 'visits'
              } — tap to stop`
            : waiting
              ? 'Auto-recording on — waiting for a visit · tap to stop'
              : 'Tap to record your day'}
        </span>
        {/* Load is the secondary, setup action — tucked to the side. */}
        <button
          onClick={() => setLoaderOpen((v) => !v)}
          title="Load your day"
          style={{
            ...buttonGhost,
            position: 'absolute',
            right: 12,
            top: 16,
            padding: '6px 12px',
            fontSize: 11,
            background: loaderOpen ? 'rgba(45,206,137,0.18)' : 'transparent',
          }}
        >
          <i className="fa fa-calendar-plus" aria-hidden="true" /> Load
        </button>
      </div>
    </div>
  );
};

export default MultiRecordTray;
